/**
 * PasswordStrengthMeter — small visual nudge under the Password field on
 * Register. Three sharp segments fill left → right as the password gains
 * complexity. Pure heuristic (no zxcvbn dep) so it stays fast and offline:
 *
 *   length >= 8       : +1
 *   length >= 12      : +1 extra (so a 12-char password scores +2 from length)
 *   has lowercase     : +1
 *   has uppercase     : +1
 *   has digit         : +1
 *   has symbol        : +1
 *   has 3+ char run   : -1 (penalises "aaaa", "1111")
 *
 *   Score ≤ 2 → Weak (red)
 *   Score 3-4 → Medium (amber)
 *   Score ≥ 5 → Strong (green)
 *
 * Empty input → render nothing (we don't yell at users before they type).
 */
import React, { useMemo } from "react";

function scorePassword(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  if (/[a-z]/.test(pw)) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  if (/(.)\1{2,}/.test(pw)) s -= 1;
  return Math.max(0, s);
}

export default function PasswordStrengthMeter({ password }) {
  const { label, color, filled } = useMemo(() => {
    const s = scorePassword(password || "");
    if (!password) return { label: "", color: "#94A3B8", filled: 0 };
    if (s <= 2) return { label: "Weak", color: "#DC2626", filled: 1 };
    if (s <= 4) return { label: "Medium", color: "#D97706", filled: 2 };
    return { label: "Strong", color: "#059669", filled: 3 };
  }, [password]);

  if (!password) return null;

  return (
    <div className="mt-2" data-testid="password-strength-meter">
      <div className="flex gap-1.5" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-sm transition-colors"
            style={{ background: i < filled ? color : "#E2E8F0" }}
          />
        ))}
      </div>
      <div
        className="text-[11px] font-mono-rm mt-1.5"
        style={{ color, fontWeight: 700 }}
        data-testid="password-strength-label"
        aria-live="polite"
      >
        Password strength: {label}
      </div>
    </div>
  );
}
